import { AsYouType, type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

const SUPPORTED_PHONE_COUNTRIES: CountryCode[] = ['US', 'AU', 'CA'];

const parseSupportedPhoneNumber = (value: string) => {
  const withPlus = value.trim().startsWith('+') ? value.trim() : `+${value.replace(/\D/g, '')}`;
  const directParse = parsePhoneNumberFromString(withPlus);

  if (directParse) {
    return directParse;
  }

  for (const country of SUPPORTED_PHONE_COUNTRIES) {
    const parsed = parsePhoneNumberFromString(value, country);
    if (parsed) {
      return parsed;
    }
  }

  return undefined;
};

const formatUsNationalDigits = (nationalDigits: string): string => {
  if (nationalDigits.length <= 3) {
    return `+1 (${nationalDigits}`;
  }

  if (nationalDigits.length <= 6) {
    return `+1 (${nationalDigits.slice(0, 3)}) ${nationalDigits.slice(3)}`;
  }

  return `+1 (${nationalDigits.slice(0, 3)}) ${nationalDigits.slice(3, 6)}-${nationalDigits.slice(6, 10)}`;
};

const isSupportedPhoneCountry = (country: CountryCode | undefined): boolean => {
  if (!country) {
    return false;
  }

  return SUPPORTED_PHONE_COUNTRIES.includes(country);
};

/**
 * Australian international numbers are +61 + 9 digits with no trunk zero.
 * Leading zeros after 61 (e.g. +61 03…) are stripped automatically.
 */
const stripAustralianTrunkZeros = (digits: string): string => {
  if (!digits.startsWith('61') || digits.length <= 2) {
    return digits;
  }

  return `61${digits.slice(2).replace(/^0+/, '')}`;
};

/**
 * Formats a phone number for display in US (+1 (XXX) XXX-XXXX) or AU (+61 ...) form.
 */
export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) {
    return '';
  }

  const digitsOnly = stripAustralianTrunkZeros(phoneNumber.replace(/\D/g, ''));
  if (!digitsOnly) {
    return '';
  }

  const normalizedValue = `+${digitsOnly}`;
  const parsed = parseSupportedPhoneNumber(normalizedValue) ?? parseSupportedPhoneNumber(phoneNumber);

  if (parsed?.isValid() && isSupportedPhoneCountry(parsed.country)) {
    if (parsed.country === 'US' || parsed.country === 'CA') {
      return formatUsNationalDigits(parsed.nationalNumber);
    }

    return parsed.formatInternational();
  }

  if (digitsOnly.startsWith('61')) {
    return new AsYouType('AU').input(normalizedValue);
  }

  if (digitsOnly.startsWith('1')) {
    return formatUsNationalDigits(digitsOnly.slice(1, 11));
  }

  return new AsYouType().input(normalizedValue);
};

/**
 * Converts a phone value (masked or unmasked) to E.164 server format when valid.
 * Incomplete values keep a progressive +digits form for controlled inputs.
 * Australian trunk zeros after +61 are stripped.
 */
export const toServerPhoneNumber = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  const digits = stripAustralianTrunkZeros(value.replace(/\D/g, ''));
  if (!digits) {
    return '';
  }

  const normalizedValue = `+${digits}`;
  const parsed = parseSupportedPhoneNumber(normalizedValue) ?? parseSupportedPhoneNumber(value);
  if (parsed?.isValid() && isSupportedPhoneCountry(parsed.country)) {
    return parsed.format('E.164');
  }

  return normalizedValue;
};

/**
 * Returns true when the value is effectively empty: no digits or only a country calling code.
 * Used for validation / save normalization — not while accepting progressive input
 * (clearing on +61 would block Australian number entry).
 */
export const isPhoneValueEmpty = (value: string | null | undefined): boolean => {
  if (!value) {
    return true;
  }

  const digits = value.replace(/\D/g, '');
  if (!digits) {
    return true;
  }

  return digits === '1' || digits === '61';
};

/**
 * Converts mask input to a stored phone value for controlled fields.
 * Keeps progressive country codes (+1 / +61) so the user can continue typing.
 * Returns empty string only when there are no digits.
 */
export const toProgressivePhoneNumber = (value: string | null | undefined): string => {
  const serverPhone = toServerPhoneNumber(value);
  return serverPhone.replace(/\D/g, '') ? serverPhone : '';
};

/**
 * Returns true when the value is a complete supported phone number (US, CA, or AU).
 */
export const isValidPhoneNumberValue = (value: string | null | undefined): boolean => {
  if (isPhoneValueEmpty(value) || !value) {
    return false;
  }

  const parsed = parseSupportedPhoneNumber(value);
  return Boolean(parsed?.isValid() && isSupportedPhoneCountry(parsed.country));
};
